/**
 * MIN-294 — the ai app's thread page composes `AgentChat.Root` +
 * `AgentChat.Settings` + `AgentChat.Thread`/`PromptInput` (+ the settings
 * parts in the composer's footer) instead of hand-rolling its own `useChat`
 * wiring, its own history rendering and inline model/system-prompt/usage
 * controls (all deleted — see the issue's "Retiring the MIN-293 fork").
 *
 * One bit of route-specific wiring is left, which `Root` itself has no
 * opinion on: `AutoFocusComposer` focuses the composer on mount, mirroring
 * `apps/provider`'s `OverlayAutoFocus`. The `sessionStorage` first-message
 * hand-off this route used to read is gone — the new-chat page composes
 * `Root` too now, and `Root`'s no-thread path sends the first message
 * straight into the thread it creates, so there is nothing to hand off.
 *
 * The audit's "selectorsLocked, then discarded" note is preserved for free
 * here, not re-implemented: `Root`'s established-thread path already ignores
 * whatever `Settings` reports at submit time (model/system-prompt choice only
 * takes effect on the new-chat page), so this route doesn't special-case it.
 */
import { AgentChat, chatMessageQueries, chatThreadQueries } from "@repo/agent-chat";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { AutoFocusComposer } from "@/components/auto-focus-composer";
import { aiModelQueries } from "@/hooks/queries/ai-models";
import { API_BASE_URL } from "@/lib/api-client";
import { medplum } from "@/lib/medplum";

export const Route = createFileRoute("/chat/_layout/$threadId")({
  component: ChatThreadView,
});

function ChatThreadView() {
  const { threadId } = Route.useParams();
  const navigate = useNavigate();

  const { data: thread } = useQuery(chatThreadQueries.byId(medplum, API_BASE_URL, threadId));
  const { data: aiModels = [] } = useQuery(aiModelQueries.list());
  const { data: threadMessages = [] } = useQuery(chatMessageQueries.byThread(medplum, API_BASE_URL, threadId));

  const models = useMemo(() => aiModels.map((m) => m.model_identifier), [aiModels]);

  const usedTokens = useMemo(
    () => threadMessages.reduce((total, m) => total + (m.usage ? m.usage.inputTokens + m.usage.outputTokens : 0), 0),
    [threadMessages],
  );

  return (
    <AgentChat.Root
      auth={medplum}
      baseUrl={API_BASE_URL}
      onThreadChange={(id) => navigate({ params: id ? { threadId: id } : {}, to: id ? "/chat/$threadId" : "/chat" })}
      threadId={threadId}
    >
      <AgentChat.Settings model={thread?.model ?? ""} models={models} onModelChange={() => {}}>
        <AutoFocusComposer />
        <AgentChat.Thread className="flex-1" />
        <div
          className="pt-4 pb-4 sticky bottom-0 z-10 w-full flex justify-center"
          style={{ background: "linear-gradient(to top, var(--background) 90%, transparent 100%)" }}
        >
          <div className="w-full">
            <AgentChat.PromptInput
              footer={
                <>
                  <AgentChat.ModelSelector />
                  <AgentChat.Context usedTokens={usedTokens} />
                </>
              }
              placeholder="Reply..."
            />
          </div>
        </div>
      </AgentChat.Settings>
    </AgentChat.Root>
  );
}
